/**
 * Sebutan unit terkecil (owner, 2026-10-04; uji D5, B20, C27, C28): the word a parish uses for
 * its smallest unit. Pengaturan paroki stores it in `pengaturan_paroki.sebutan_unit_dasar`:
 * "Lingkungan" by default, "Kombas", or a word of the parish's own. Every place SATU means that
 * unit says this word: the strings carry it as a placeholder, `{unit}` where it stands as a
 * name or opens a sentence ("{unit} tujuan", "Ketua {unit}") and `{unitKecil}` inside a
 * sentence ("Pindahkan {unitKecil}", "Belum ada keluarga di {unitKecil} ini."). Names people
 * typed ("Lingkungan St. Maria") stay as typed; a stored role code (`ketua_lingkungan`) never
 * changes, only the words that show it.
 *
 * Pure: the screens read the parish's word through the app's hook, prints and the worker
 * through these functions with the word they read.
 */

export const SEBUTAN_UNIT_BAWAAN = 'Lingkungan'

export interface SebutanUnit {
  /** As a name or at a sentence's start: "Lingkungan", "Kombas". */
  readonly unit: string
  /** Inside a sentence: "lingkungan", "kombas"; a word in capitals ("KBG") keeps them. */
  readonly unitKecil: string
}

const bawaan = new Map<string, SebutanUnit>()

/** The word as the strings use it, from the stored setting (none, or blank: "Lingkungan"). */
export function sebutanUnit(tersimpan?: string | null): SebutanUnit {
  const kata = (tersimpan ?? '').replace(/\s+/g, ' ').trim() || SEBUTAN_UNIT_BAWAAN
  const ada = bawaan.get(kata)
  if (ada !== undefined) return ada
  const sebutan: SebutanUnit = {
    unit: kata.charAt(0).toLocaleUpperCase('id-ID') + kata.slice(1),
    // Two capitals in a row read as an abbreviation, which keeps its capitals in a sentence.
    unitKecil: /\p{Lu}{2}/u.test(kata) ? kata : kata.toLocaleLowerCase('id-ID'),
  }
  bawaan.set(kata, sebutan)
  return sebutan
}

/** The default word, for a view with no parish in context (a diocese view). */
export const SEBUTAN_BAWAAN: SebutanUnit = sebutanUnit(null)

const PENANDA = /\{(unit|unitKecil)\}/g

function isiTeks(teks: string, s: SebutanUnit): string {
  return teks.includes('{unit')
    ? teks.replace(PENANDA, (_, nama: string) => (nama === 'unit' ? s.unit : s.unitKecil))
    : teks
}

const tersimpan = new WeakMap<object, Map<string, unknown>>()

/**
 * A string, or a tree of strings (a part of `uiStrings` or `suratStrings`), with `{unit}` and
 * `{unitKecil}` filled; every other placeholder stays for `format`. A tree comes back as the
 * same object for the same word, so a screen's memo sees it unchanged between renders.
 */
export function isiUnit<T>(nilai: T, s: SebutanUnit): T {
  if (typeof nilai === 'string') return isiTeks(nilai, s) as T
  if (typeof nilai !== 'object' || nilai === null) return nilai
  const kunci = `${s.unit}\u0000${s.unitKecil}`
  let perKata = tersimpan.get(nilai)
  if (perKata === undefined) {
    perKata = new Map()
    tersimpan.set(nilai, perKata)
  }
  const ada = perKata.get(kunci)
  if (ada !== undefined) return ada as T
  const hasil = Array.isArray(nilai)
    ? nilai.map((v: unknown) => isiUnit(v, s))
    : Object.fromEntries(Object.entries(nilai).map(([k, v]) => [k, isiUnit(v, s)]))
  perKata.set(kunci, hasil)
  return hasil as T
}

function lepasRegex(kata: string): string {
  return kata.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * A unit's name without the word that says what kind of unit it is, so a template that says
 * the word itself ("{unit} {lingkungan}") says it once: "Lingkungan St. Maria" and, in a parish
 * that says Kombas, "Kombas St. Maria" both give "St. Maria". `jenis` adds other kinds the
 * name may open with (Stasi, Paroki). A name that is only the word stays whole.
 */
export function tanpaSebutanUnit(
  nama: string,
  s: SebutanUnit = SEBUTAN_BAWAAN,
  jenis: readonly string[] = [],
): string {
  const kata = [SEBUTAN_UNIT_BAWAAN, s.unit, ...jenis].map(lepasRegex)
  return nama.replace(new RegExp(`^(?:${kata.join('|')})\\s+(?=\\S)`, 'i'), '')
}
